import styled from '@emotion/styled';
import { AnnouncementPanel, Typography } from '@innovamat/glimmer-components';
import type { ReactNode } from 'react';
import { StyledBody } from './styled-text';
import { TranslatorComponent } from './translator-component';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  a {
    color: ${({ theme }) => theme.tokens.color.specific.element.link.value};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const QuestionSection = styled(Section)`
  gap: 16px;
  white-space: pre-line;

  li::marker {
    font-size: 12px;
  }

  li {
    margin-left: 24px;
    margin-bottom: 8px;
  }

  ul {
    margin-top: -8px;
    margin-bottom: 0;
  }
`;

type DrawerSectionProps = {
  announcementTranslationKey?: string;
  announcementType?: 'info' | 'warning' | 'error' | 'success';
  bodyTranslationKey?: string;
  children?: ReactNode;
  isQuestionSection?: boolean;
  title?: string;
  titleTranslationKey?: string;
  onPersonalizeLinkClick?: () => void;
  onReportLinkClick?: () => void;
};

export const DrawerSection = ({
  announcementTranslationKey,
  announcementType = 'info',
  bodyTranslationKey,
  children,
  isQuestionSection = false,
  title,
  titleTranslationKey,
  onPersonalizeLinkClick,
  onReportLinkClick,
}: DrawerSectionProps): JSX.Element => {
  const SectionComponent = isQuestionSection ? QuestionSection : Section;

  return (
    <SectionComponent>
      {(title || titleTranslationKey) && (
        <Typography.Subtitle1>
          {titleTranslationKey ? (
            <TranslatorComponent translationKey={titleTranslationKey} />
          ) : (
            title
          )}
        </Typography.Subtitle1>
      )}
      {bodyTranslationKey && (
        <StyledBody>
          <TranslatorComponent
            translationKey={bodyTranslationKey}
            onPersonalizeLinkClick={onPersonalizeLinkClick}
            onReportLinkClick={onReportLinkClick}
          />
        </StyledBody>
      )}
      {children}
      {announcementTranslationKey && (
        <AnnouncementPanel
          text={
            <TranslatorComponent translationKey={announcementTranslationKey} />
          }
          type={announcementType}
          canClose={false}
        />
      )}
    </SectionComponent>
  );
};
